import { useCallback, useEffect, useRef, useState } from "react";
import type { LayoutChangeEvent, ViewStyle } from "@-/com.npmjs.react-native";
import {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type AnimatedStyle,
} from "@-/com.npmjs.react-native-reanimated";
import { usePrimaryControlsStripExpansionHasContent } from "#use-primary-controls-strip-expansion-has-content";
import { usePrimaryControlsStripExpansionState } from "#use-primary-controls-strip-expansion-state";

const EXPANSION_ANIMATION_DURATION_MS = 220;

export type PrimaryControlsStripExpansionAnimatedHeight = {
  /** `onLayout` handler for the absolutely-positioned content probe. */
  onContentLayout: (event: LayoutChangeEvent) => void;
  /**
   * Animated style for the `overflow:hidden` wrapper `<Animated.View>`. Typed as
   * `AnimatedStyle<ViewStyle>` rather than `ReturnType<typeof useAnimatedStyle>` (an opaque
   * `AnimatedStyleHandle`) so it stays assignable to the View `style` prop through the hook
   * boundary, matching the inline inference the callsites used to get.
   */
  wrapperStyle: AnimatedStyle<ViewStyle>;
};

/**
 * Shared expand/collapse height animation for the header-expansion strip. Both the in-header
 * strip (default/portrait layout, in `PrimaryControlsStrip`) and the slide-down `PrimaryControlsStripExpansionPanel`
 * (native landscape) render the same content inside an `overflow:hidden` wrapper that animates
 * between 0 and the content's intrinsic height; this hook is the single source of truth for that
 * motion so the two callsites can't drift apart.
 *
 * The content stays mounted unconditionally (toggling shouldn't remount it and flash default
 * state); we measure its intrinsic height via the returned `onContentLayout` probe — ignoring
 * 0-height measurements so a hidden/empty probe doesn't clobber a real value — then animate the
 * wrapper to that height when open and 0 when closed.
 *
 * The stored `isExpanded` flag lives above the navigator and survives navigation, so a screen
 * with no published expansion content can inherit a stale `true` (and a stale `contentHeight`
 * from the previous screen, since the probe ignores 0-height measurements). Gating on
 * `hasContent` collapses to 0 whenever there's nothing to show — without touching the flag, so it
 * takes effect again on a screen that does publish content — rather than leaving a blank strip.
 *
 * We only animate when the *toggle* drives the change (`isExpanded` flipping from a button press).
 * When the strip opens/closes because `hasContent` changed — i.e. navigating between a screen that
 * publishes expansion content and one that doesn't — we snap to the target height immediately, so
 * the strip appears/disappears at once instead of sliding in/out as you switch tabs.
 */
export function usePrimaryControlsStripExpansionAnimatedHeight(): PrimaryControlsStripExpansionAnimatedHeight {
  const { isExpanded } = usePrimaryControlsStripExpansionState();
  const hasContent = usePrimaryControlsStripExpansionHasContent();
  const [contentHeight, setContentHeight] = useState(0);
  const animatedHeight = useSharedValue(0);
  const prevHasContentRef = useRef(hasContent);

  const isStripOpen = isExpanded && hasContent;

  useEffect(() => {
    const target = isStripOpen ? contentHeight : 0;
    // `hasContent` flips on navigation between tabs; that transition should be instant, not
    // animated. A press of the expand/collapse toggle only changes `isExpanded`, so `hasContent`
    // is unchanged and we animate as before.
    const hasContentChanged = prevHasContentRef.current !== hasContent;
    prevHasContentRef.current = hasContent;

    animatedHeight.set(
      hasContentChanged
        ? target
        : withTiming(target, { duration: EXPANSION_ANIMATION_DURATION_MS }),
    );
  }, [animatedHeight, contentHeight, hasContent, isStripOpen]);

  const wrapperStyle = useAnimatedStyle(() => ({
    height: animatedHeight.get(),
    overflow: "hidden",
  }));

  const onContentLayout = useCallback((event: LayoutChangeEvent) => {
    const measured = event.nativeEvent.layout.height;
    if (measured > 0) {
      setContentHeight(measured);
    }
  }, []);

  return { onContentLayout, wrapperStyle };
}
